import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthService } from '@/auth/services';
import { DataTableComponent } from '@/components';
import {
    DataTableColumnType,
    FilterType
} from '@/enums';
import {
    DataTableColumn,
    DistrictMapping
} from '@/interfaces';
import {
    DistrictMappingsService,
    DistrictsService
} from '@/service';


@Component({
    selector: 'app-district-listing',
    standalone: true,
    templateUrl: 'listing.component.html',
    imports: [DataTableComponent]
})
export class DistrictListingComponent {
    reloadTable$ = new Subject<void>();
    columns: DataTableColumn<DistrictMapping>[] = [
        {
            field: 'is_mapped',
            header: 'Mapped',
            filterType: FilterType.BOOLEAN,
            filterInToolbar: true,
            tableVisible: false,
            sortable: false
        },
        {
            field: 'mapped_district.district',
            header: 'Mapped District',
            filterType: FilterType.AUTOCOMPLETE,
            autocompleteCompleteMethod: (q, context) => this.districtMappingAutocomplete('mapped_district.district', q, context),
            editable: true,
            editType: FilterType.AUTOCOMPLETE,
            editAutocompleteMethod: (e: string) => this.districtsService.autocomplete('districts', e).pipe(map((res) => res.data ?? res)),
            onEditSave: (value: any, row: DistrictMapping) => this.edit(value, row)
        },
        {
            field: 'district_name',
            header: 'District (Raw)',
            type: DataTableColumnType.LINK,
            link: (row: DistrictMapping) => (row.mapped_district_id ? `/districts/${row.id}` : null),
            filterType: FilterType.AUTOCOMPLETE,
            autocompleteCompleteMethod: (q, context) => this.districtMappingAutocomplete('district_name', q, context)
        },
        {
            field: 'rates_count',
            header: 'Number of Rates (Raw)',
            type: DataTableColumnType.NUMBER,
            filterType: FilterType.NUMERIC
        },
        {
            field: 'state',
            header: 'State',
            formatter: (row: DistrictMapping) => this.effectiveState(row),
            filterType: FilterType.AUTOCOMPLETE,
            matchMode: 'in',
            autocompleteCompleteMethod: (q, context) => this.districtMappingAutocomplete('states', q, context)
        },
        {
            field: 'county',
            header: 'County',
            formatter: (row: DistrictMapping) => this.effectiveCounty(row),
            filterType: FilterType.AUTOCOMPLETE,
            matchMode: 'in',
            autocompleteCompleteMethod: (q, context) => this.districtMappingAutocomplete('counties', q, context)
        },
        {
            field: 'mapped_district.address',
            header: 'Address'
        },
        {
            field: 'mapped_district.phone',
            header: 'Phone',
            type: DataTableColumnType.PHONE
        },
        {
            field: 'actions',
            header: '',
            type: DataTableColumnType.ACTIONS,
            sortable: false,
            filterable: false,
            actions: [
                {
                    icon: 'pi pi-eye',
                    label: 'View Rates',
                    command: (row) =>
                        this.router.navigate(['/rates'], {
                            queryParams: {
                                district_name: row.mapped_district?.district ?? row.district_name
                            }
                        }),
                    showIf: (row) => this.authService.hasPermission('rates.rates.read') && !!row.mapped_district_id
                }
            ]
        }
    ];

    constructor(
        public authService: AuthService,
        private router: Router,
        private service: DistrictMappingsService,
        private districtsService: DistrictsService,
        private messageService: MessageService
    ) {}

    loadData = (params: Record<string, any>) => this.service.listing(params);

    exportData = (params: Record<string, any>) => this.service.export(params);

    districtMappingAutocomplete(
        field: 'district_name' | 'mapped_district.district' | 'states' | 'counties',
        query: string,
        context: Record<string, any> = {}
    ) {
        return this.service.autocomplete(field, query, context);
    }

    private effectiveCounty(row: DistrictMapping): string | null {
        return row.mapped_district?.county || row.county || null;
    }

    private effectiveState(row: DistrictMapping): string | null {
        return row.mapped_district?.state || row.state || null;
    }

    edit(value: string, row: DistrictMapping): void {
        this.service.edit(row.id, { mapped_district_id: value }).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    detail: 'District Mapping has been updated successfully.'
                });
                this.reloadTable$.next();
            },
            error: (err) => {
                if (err.error?.error) {
                    this.messageService.add({
                        severity: 'error',
                        detail: err.error?.error
                    });
                }
            }
        });
    }
}
